<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import AppCard from '@/components/ui/AppCard.vue'
import AppDialog from '@/components/ui/Dialog.vue'
import Button from 'primevue/button'
import Tag from 'primevue/tag'
import { useVolverAlListado } from '@/composables/useVolverAlListado'
import { useAuthStore } from '@/stores/auth'
import { roleLabel, roleSeverity } from '@/utils/roles'
import { formatShortDate } from '@/utils/format'
import { usersApi, type CrmUser } from '@/services/users'

/**
 * Ficha de un usuario.
 *
 * La acción de la pantalla es activar o desactivar, no borrar. Un usuario tiene
 * cotizaciones y borrarlo dejaría el histórico de ventas sin autor, con su
 * `user_id` en `null` y fuera del ranking de asesores. Desactivar corta el acceso
 * y conserva todo lo demás, que es lo que se necesita en la práctica.
 */

const route = useRoute()
const router = useRouter()
const authStore = useAuthStore()

const user = ref<CrmUser | null>(null)
const loading = ref(true)
const loadError = ref<string | null>(null)
const toggling = ref(false)
const toggleError = ref<string | null>(null)
const confirmOpen = ref(false)

const isSelf = computed(() => user.value?.id === authStore.user?.id)

/**
 * Volver al listado **con los filtros con los que se llegó**.
 *
 * El listado lleva sus filtros en la URL —para eso está `useFiltrosEnUrl`—, así que al
 * abrir esta vista se los lleva en la query. Sin devolvérselos, quien filtra, abre una
 * ficha y vuelve se encuentra la lista entera: el filtro se aplicó y desapareció.
 *
 * «Volver» empuja una entrada **nueva**, así que el historial no salva: la entrada anterior,
 * la que tenía los filtros, deja de ser la que se está usando.
 */
const { destino: destinoListado, volver: volverAlListado } = useVolverAlListado('users.index')

function idFromRoute(): number {
  return Number(route.params.id)
}

function extractMessage(cause: unknown): string {
  const maybeAxios = cause as { response?: { data?: { error?: { message?: string } } } }
  return maybeAxios.response?.data?.error?.message ?? 'No se pudo completar la operación. Revisa tu conexión e inténtalo de nuevo.'
}

async function load(): Promise<void> {
  loading.value = true
  loadError.value = null

  try {
    const { data } = await usersApi.get(idFromRoute())
    user.value = data.data
  } catch (cause: unknown) {
    loadError.value = extractMessage(cause)
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  void load()
})

watch(() => route.params.id, () => {
  void load()
})

async function onToggleActive(): Promise<void> {
  toggling.value = true
  toggleError.value = null

  try {
    const { data } = await usersApi.toggleActive(idFromRoute())
    user.value = data.data
  } catch (cause: unknown) {
    toggleError.value = extractMessage(cause)
  } finally {
    toggling.value = false
    confirmOpen.value = false
  }
}
</script>

<template>
  <div class="space-y-6 animate-fade-in">
    <header class="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
      <div>
        <h1 class="text-2xl font-semibold text-surface-900 dark:text-surface-100">
          {{ user?.name ?? 'Usuario' }}
        </h1>
        <p
          v-if="user"
          class="mt-1 font-mono text-sm text-surface-500"
        >
          {{ user.username }}
        </p>
      </div>

      <div class="flex flex-wrap gap-2">
        <Button
          label="Volver"
          icon="pi pi-arrow-left"
          severity="secondary"
          text
          @click="volverAlListado"
        />
        <Button
          label="Editar"
          icon="pi pi-pencil"
          severity="secondary"
          outlined
          @click="router.push({ name: 'users.edit', params: { id: String(idFromRoute()) } })"
        />
        <Button
          v-if="user !== null"
          :label="user.active ? 'Desactivar' : 'Reactivar'"
          :icon="user.active ? 'pi pi-user-minus' : 'pi pi-user-plus'"
          :severity="user.active ? 'danger' : 'success'"
          :loading="toggling"
          @click="confirmOpen = true"
        />
      </div>
    </header>

    <div
      v-if="loadError"
      class="flex flex-col items-center gap-3 py-12 text-center"
      role="alert"
    >
      <i class="pi pi-exclamation-triangle text-4xl text-amber-500" />
      <p class="font-medium text-surface-900 dark:text-surface-100">
        No se pudo cargar el usuario
      </p>
      <p class="max-w-md text-sm text-surface-500 dark:text-surface-400">
        {{ loadError }}
      </p>
      <Button
        label="Reintentar"
        icon="pi pi-refresh"
        severity="secondary"
        @click="load"
      />
    </div>

    <div
      v-else-if="loading"
      class="py-12 text-center text-surface-500"
      aria-busy="true"
    >
      <i class="pi pi-spin pi-spinner text-2xl" />
      <p class="mt-2 text-sm">
        Cargando usuario…
      </p>
    </div>

    <template v-else-if="user">
      <p
        v-if="toggleError"
        class="text-sm text-red-600 dark:text-red-400"
        role="alert"
      >
        {{ toggleError }}
      </p>

      <AppCard>
        <template #header>
          <h2 class="text-lg font-semibold text-surface-900 dark:text-surface-100">
            Cuenta
          </h2>
        </template>

        <dl class="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <dt class="text-sm text-surface-500 dark:text-surface-400">
              Nombre
            </dt>
            <dd class="text-surface-900 dark:text-surface-100">
              {{ user.name }}
            </dd>
          </div>
          <div>
            <dt class="text-sm text-surface-500 dark:text-surface-400">
              Usuario
            </dt>
            <dd class="font-mono text-sm text-surface-900 dark:text-surface-100">
              {{ user.username }}
            </dd>
          </div>
          <div>
            <dt class="text-sm text-surface-500 dark:text-surface-400">
              Email
            </dt>
            <dd class="text-surface-900 dark:text-surface-100">
              {{ user.email ?? '—' }}
            </dd>
          </div>
          <div>
            <dt class="text-sm text-surface-500 dark:text-surface-400">
              Teléfono
            </dt>
            <dd class="text-surface-900 dark:text-surface-100">
              {{ user.phone ?? '—' }}
            </dd>
          </div>
          <div>
            <dt class="text-sm text-surface-500 dark:text-surface-400">
              Último acceso
            </dt>
            <dd class="text-surface-900 dark:text-surface-100">
              {{ user.last_login_at ? formatShortDate(user.last_login_at) : 'Nunca ha entrado' }}
            </dd>
          </div>
          <div>
            <dt class="text-sm text-surface-500 dark:text-surface-400">
              Alta
            </dt>
            <dd class="text-surface-900 dark:text-surface-100">
              {{ formatShortDate(user.created_at) }}
            </dd>
          </div>
        </dl>
      </AppCard>

      <AppCard>
        <template #header>
          <h2 class="text-lg font-semibold text-surface-900 dark:text-surface-100">
            Permisos
          </h2>
        </template>

        <div class="flex flex-wrap gap-1">
          <Tag
            v-if="user.roles.length === 0"
            value="Sin rol asignado"
            severity="secondary"
          />
          <Tag
            v-for="role in user.roles"
            :key="role"
            :value="roleLabel(role)"
            :severity="roleSeverity(role)"
          />
        </div>

        <p class="mt-3 text-sm text-surface-500 dark:text-surface-400">
          Estos permisos salen del rol y son los mismos que aplica el backend. La
          lista se muestra para que se entienda por qué una sección aparece o no,
          no para editarla aquí.
        </p>

        <ul class="mt-3 grid grid-cols-1 gap-1 sm:grid-cols-2">
          <li
            v-for="permission in user.permissions"
            :key="permission"
            class="flex items-center gap-2 font-mono text-xs text-surface-600 dark:text-surface-300"
          >
            <i
              class="pi pi-check text-emerald-600"
              aria-hidden="true"
            />
            {{ permission }}
          </li>
        </ul>
      </AppCard>

      <AppDialog
        v-model:visible="confirmOpen"
        :title="user.active ? 'Desactivar usuario' : 'Reactivar usuario'"
        width="28rem"
        fullscreen-mobile
      >
        <p class="text-sm text-surface-600 dark:text-surface-300">
          <template v-if="user.active">
            {{ user.name }} dejará poder iniciar sesión. Sus cotizaciones se
            conservan y siguen contando en el histórico de ventas.
          </template>
          <template v-else>
            {{ user.name }} podrá volver a entrar al CRM con su mismo usuario y
            contraseña.
          </template>
        </p>

        <p
          v-if="isSelf"
          class="mt-3 text-sm text-amber-700 dark:text-amber-300"
        >
          Estás desactivando tu propia cuenta: perderás el acceso en esta sesión.
        </p>

        <template #footer>
          <Button
            label="Cancelar"
            severity="secondary"
            text
            @click="confirmOpen = false"
          />
          <Button
            :label="user.active ? 'Desactivar' : 'Reactivar'"
            :severity="user.active ? 'danger' : 'success'"
            :loading="toggling"
            @click="onToggleActive"
          />
        </template>
      </AppDialog>
    </template>
  </div>
</template>
